import { CssBaseline, ThemeProvider, createTheme } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { themeSettings } from "theme";
import "./i18n"; // Initialize i18n
import "leaflet/dist/leaflet.css"; // Leaflet CSS for OpenStreetMap
// Admin Scenes
import Layout from "scenes/layout";
import Customers from "scenes/customers";
import WhatsAppSettings from "scenes/whatsapp-settings";
import WhatsAppChat from "scenes/whatsapp-settings/WhatsAppChat";
import WhatsAppChatMenu from "scenes/whatsappChat";
import Packages from "scenes/packages";
import Users from "scenes/users";
import Scan from "scenes/scan";
import WhatsApp from "scenes/whatsapp";
import WhatsAppBulk from "scenes/whatsapp-bulk";

import Tracking from "scenes/tracking";
import Gallery from "scenes/gallery";
import SalesDocuments from "scenes/salesDocuments";
// Customer Scenes
//Public Scenes
import HomePage from "scenes/public/home";
import ServicesPage from "scenes/public/services";
import AboutPage from "scenes/public/about";
import ContactPage from "scenes/public/contact";
import TrackPackagePage from "scenes/public/trackpackage";
import UserLogin from "scenes/public/userLogin";
import CustomerLogin from "scenes/public/login";
import WhatsappLogin from "scenes/public/login/whatsappLogin";
import CustomerSignup from "scenes/public/signup";

import { logout } from "utils";
import AuthVerify from "utils/AuthVerify";
import BulkMove from "scenes/bulkmove";
import PackageBulkMove from "scenes/packageBulkMove";
import PackageChangeRequests from "scenes/packageChangeRequests";
import DepartureBatch from "scenes/departureBatch";
import ParcelMonitoring from "scenes/parcelMonitoring";
import UnassginedPackage from "scenes/unassignedPackages";
import MatchPackage from "scenes/matchPackage";
import AgentManagement from "scenes/agentManagement";
import { user } from "localStorage";
import ChromeExtension from "scenes/chromeExtension";
import StaffCalculator from "scenes/calculator";
import ProfilePage from "scenes/profile";
import Stations from "scenes/stations";
import { AppConstants, ConstantsProvider } from "utils/const";
import PermittedRoute from "components/PermittedRoute";
import { hasPageAccess } from "utils/pagePermissions";

// customer scenes
import CustomerInfo from "scenes/customers/customerInfo";
import CustomerPortal from "scenes/customerPortal";
import SetPassword from "scenes/customerPortal/setPassword";

import CustomerLayout from "scenes/layout/customerLayout";
import MyProfile from "scenes/customerPortal/myProfile";
import ClientTracking from "scenes/customerPortal/tracking";
import WalletDashboard from "scenes/customerPortal/wallet";
import WalletTopup from "scenes/customerPortal/wallet/topup";
import TopupRequests from "scenes/customerPortal/wallet/requests";
import TransactionHistory from "scenes/customerPortal/wallet/history";
import CustomerWithdrawal from "scenes/customerPortal/wallet/withdrawal";
import WalletWithdrawal from "scenes/wallet/withdrawal";
import WalletAccounts from "scenes/wallet/accounts";
import AccountTransactions from "scenes/wallet/accountTransactions";
import FinancialDashboard from "scenes/financial";
import FinancialApprovals from "scenes/financial/approvals";
import SlipBotHistory from "scenes/slipbotHistory";
import Settings from "scenes/settings";
import FinancialWallets from "scenes/financial/wallets";
import FinancialTransactions from "scenes/financial/transactions";
import FinancialOutstandingDetail from "scenes/financial/outstandingDetail";
import FinancialOutstandingParcels from "scenes/financial/outstandingParcels";
import BranchCash from "scenes/branchCash";
import BranchCashStation from "scenes/branchCash/station";
import WalletExchange from "scenes/customerPortal/wallet/exchange";
import NotificationPage from "pages/NotificationPage";
import NotificationProvider from "providers/NotificationProvider";

// import Register from "scenes/public/register";
import ResgisterScene from "scenes/public/register";
import ClientBooking from "scenes/customerPortal/booking";
import MyParcels from "scenes/customerPortal/package/MyParcels";
import CustomerNotifications from "scenes/customerPortal/notifications";
import ShippingCalculator from "scenes/customerPortal/calculator";
import ScrollToTop from "components/ScrollToTop";
import ForgotPassword from "scenes/public/forgotPassword";

// Package Change Requests is now a tab of /packages. Keep the old URL (used by
// notification links, ?request=<id>) working: redirect there, or — for a role
// with change-request access but no Packages page — render it standalone.
const PackageChangeRequestsRoute = () => {
  const location = useLocation();
  if (hasPageAccess(user(), "packages")) {
    const extra = location.search ? `&${location.search.slice(1)}` : "";
    return <Navigate to={`/packages?tab=change-requests${extra}`} replace />;
  }
  return <PermittedRoute page="packages/change-requests" element={<PackageChangeRequests />} />;
};

// Old /tracking (public) and /track-package redirect to /track
const PublicTrackingRedirect = () => {
  const location = useLocation();
  return <Navigate to={`/track${location.search || ""}`} replace />;
};

function App() {
  const mode = useSelector((state) => state.global.mode);
  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);
  const _user = user();

  const [customer, setCustomer] = useState(() => {
    const storedCustomer = localStorage.getItem("customer");
    return storedCustomer ? JSON.parse(storedCustomer) : null;
  });

  useEffect(() => {
    const handleStorageChange = () => {
      const updatedCustomer = localStorage.getItem("customer");
      console.log("Storage updated:", updatedCustomer); // Debugging log
      setCustomer(updatedCustomer ? JSON.parse(updatedCustomer) : null);
    };

    // Listen for changes to localStorage
    window.addEventListener("storage", handleStorageChange);

    // Cleanup listener on component unmount
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  const updateCustomerState = (newCustomer) => {
    setCustomer(newCustomer);
  };

  const dispatch = useDispatch();

  const logOut = useCallback(() => {
    dispatch(logout());
  }, [dispatch]);

  return (
    <div className="app">
      <BrowserRouter>
        <ScrollToTop />
        <ConstantsProvider value={AppConstants}>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <NotificationProvider>
              {_user && _user.role ? (
                <>
                  <Routes>
                  <Route element={<Layout />}>
                    <Route path="*" element={<Scan />} />
                    <Route path="/scan" element={<Scan />} />
                    <Route path="/packages" element={<PermittedRoute page="packages" element={<Packages />} />} />
                    <Route path="/customers" element={<PermittedRoute page="customers" element={<Customers />} />} />
                    <Route path="/customer/:id" element={<PermittedRoute page="customers" element={<CustomerInfo />} />} />
                    <Route path="/whatsappgroup" element={<PermittedRoute page="whatsappgroup" element={<WhatsAppSettings />} />} />
                    <Route path="/whatsapp-chat/:groupId" element={<PermittedRoute page="whatsappgroup" element={<WhatsAppChat />} />} />
                    <Route path="/wallet/withdrawal" element={<PermittedRoute page="wallet/withdrawal" element={<WalletWithdrawal />} />} />
                    {/* Top-up/shipping approvals live on their own page now — Financial Dashboard
                        → Approvals is a separate page+permission, not a tab. Old links/notifications redirect there. */}
                    <Route path="/wallet/approval" element={<Navigate to="/financial/approvals" replace />} />
                    <Route path="/wallet/history" element={<Navigate to="/financial/approvals" replace />} />
                    <Route path="/wallet/report" element={<Navigate to="/financial" replace />} />
                    <Route path="/wallet/accounts" element={<PermittedRoute page="wallet/accounts" element={<WalletAccounts />} />} />
                    <Route path="/wallet/accounts/:accountId/transactions" element={<PermittedRoute page="wallet/accounts" element={<AccountTransactions />} />} />
                    {/* Purchase Order / Refund moved into dialogs on the Customer Wallets
                        account screen (like Top-up) — old direct links redirect there. */}
                    <Route path="/wallet/purchase-order" element={<Navigate to="/wallet/accounts" replace />} />
                    <Route path="/wallet/refund" element={<Navigate to="/wallet/accounts" replace />} />
                    <Route path="/wallet/exchange-rate" element={<Navigate to="/settings" replace />} />
                    <Route path="/financial" element={<PermittedRoute page="financial" element={<FinancialDashboard />} />} />
                    <Route path="/financial/approvals" element={<PermittedRoute page="financial/approvals" element={<FinancialApprovals />} />} />
                    <Route path="/slipbot-history" element={<PermittedRoute page="slipbot-history" element={<SlipBotHistory />} />} />
                    <Route path="/settings" element={<PermittedRoute page="settings" element={<Settings />} />} />
                    <Route path="/financial/wallets" element={<PermittedRoute page="financial" element={<FinancialWallets />} />} />
                    <Route path="/financial/wallets/:accountId/transactions" element={<PermittedRoute page="financial" element={<FinancialTransactions />} />} />
                    <Route path="/financial/branch-cash/:stationId" element={<PermittedRoute page="financial" element={<FinancialTransactions />} />} />
                    <Route path="/financial/outstanding/:stationId" element={<PermittedRoute page="financial" element={<FinancialOutstandingDetail />} />} />
                    <Route path="/financial/outstanding/:stationId/:customerId" element={<PermittedRoute page="financial" element={<FinancialOutstandingParcels />} />} />
                    <Route path="/branch-cash" element={<PermittedRoute page="branch-cash" element={<BranchCash />} />} />
                    <Route path="/branch-cash/:stationId" element={<PermittedRoute page="branch-cash" element={<BranchCashStation />} />} />
                    <Route path="/whatsapp" element={<PermittedRoute page="whatsapp" element={<WhatsApp />} />} />
                    <Route path="/whatsappbulk" element={<PermittedRoute page="whatsappbulk" element={<WhatsAppBulk />} />} />
                    <Route path="/whatsappchat" element={<PermittedRoute page="whatsappchat" element={<WhatsAppChatMenu />} />} />

                    <Route path="/tracking" element={<PermittedRoute page="tracking" element={<Tracking />} />} />
                    <Route path="/gallery" element={<PermittedRoute page="gallery" element={<Gallery />} />} />
                    <Route path="/sales-documents" element={<PermittedRoute page="sales-documents" element={<SalesDocuments />} />} />
                    <Route path="/notifications" element={<PermittedRoute page="notifications" element={<NotificationPage />} />} />
                    <Route path="/bulkmove" element={<PermittedRoute page="bulkmove" element={<BulkMove />} />} />
                    <Route path="/packagebulkmove" element={<PermittedRoute page="packagebulkmove" element={<PackageBulkMove />} />} />
                    <Route path="/packages/change-requests" element={<PackageChangeRequestsRoute />} />
                    <Route path="/departurebatch" element={<PermittedRoute page="departurebatch" element={<DepartureBatch />} />} />
                    <Route path="/parcel-monitoring" element={<PermittedRoute page="parcel-monitoring" element={<ParcelMonitoring />} />} />
                    <Route path="/agent-settings" element={<PermittedRoute page="agent-settings" element={<AgentManagement />} />} />
                    <Route path="/unassignedpackage" element={<PermittedRoute page="unassignedpackage" element={<UnassginedPackage />} />} />
                    <Route path="/matchpackage" element={<PermittedRoute page="matchpackage" element={<MatchPackage />} />} />
                    <Route path="/users" element={<PermittedRoute page="users" element={<Users />} />} />
                    <Route path="/chrome-extension" element={<PermittedRoute page="chrome-extension" element={<ChromeExtension />} />} />
                    <Route path="/stations" element={<PermittedRoute page="stations" element={<Stations />} />} />
                    {/* No PermittedRoute: the calculator is open to every staff user (ALWAYS_VISIBLE_PAGES). */}
                    <Route path="/calculator" element={<StaffCalculator />} />
                    <Route path="/profile" element={<ProfilePage />} />
                  </Route>
                </Routes>
                <AuthVerify logOut={logOut} />
              </>
            ) : customer ? (
              <>
                <Routes
                  key={customer?.existingCustomer + customer?.emptyPassword}
                >
                  {customer.existingCustomer && customer.emptyPassword ? (
                    <>
                      <Route
                        path="/set-password"
                        element={
                          <SetPassword
                            updateCustomerState={updateCustomerState}
                          />
                        }
                      />
                      <Route
                        path="*"
                        element={
                          <SetPassword
                            updateCustomerState={updateCustomerState}
                          />
                        }
                      />
                    </>
                  ) : (
                    <>
                      <Route element={<CustomerLayout />}>
                        <Route path="/" element={<CustomerPortal />} />
                        <Route path="/myparcels" element={<MyParcels />} />
                        <Route path="/myprofile" element={<MyProfile />} />
                        <Route path="/wallet" element={<WalletDashboard />} />
                        <Route path="/wallet/topup" element={<WalletTopup />} />
                        <Route path="/wallet/requests" element={<TopupRequests />} />
                        <Route path="/wallet/history" element={<TransactionHistory />} />
                        <Route path="/wallet/withdrawal" element={<CustomerWithdrawal />} />
                        <Route path="/wallet/exchange" element={<WalletExchange />} />
                        <Route path="/customer-portal/*" element={
                          <Routes>
                            <Route path="/" element={<CustomerPortal />} />
                            <Route path="wallet" element={<WalletDashboard />} />
                            <Route path="wallet/topup" element={<WalletTopup />} />
                            <Route path="wallet/requests" element={<TopupRequests />} />
                            <Route path="wallet/history" element={<TransactionHistory />} />
                            <Route path="wallet/withdrawal" element={<CustomerWithdrawal />} />
                            <Route path="wallet/exchange" element={<WalletExchange />} />

                            <Route path="myprofile" element={<MyProfile />} />
                            <Route path="myparcels" element={<MyParcels />} />
                            <Route path="tracking" element={<ClientTracking />} />
                            <Route path="booking" element={<ClientBooking />} />
                            <Route path="calculator" element={<ShippingCalculator />} />
                          </Routes>
                        } />
                        <Route path="/tracking" element={<ClientTracking />} />
                        <Route path="/booking" element={<ClientBooking />} />
                        <Route path="/calculator" element={<ShippingCalculator />} />
                        <Route path="/contact" element={<ContactPage />} />
                        <Route path="/notifications" element={<CustomerNotifications />} />

                        <Route path="*" element={<CustomerPortal />} />
                      </Route>
                    </>
                  )}
                </Routes>
                <AuthVerify logOut={logOut} />
              </>
            ) : (
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/services" element={<ServicesPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/calculator" element={<ShippingCalculator />} />
                <Route path="/tracking" element={<PublicTrackingRedirect />} />
                <Route path="/track-package" element={<PublicTrackingRedirect />} />
                <Route path="/track" element={<TrackPackagePage />} />
                <Route path="/admin/login" element={<UserLogin />} />
                <Route
                  path="/login"
                  element={
                    <CustomerLogin updateCustomerState={updateCustomerState} />
                  }
                />
                <Route
                  path="/whatsapp-login"
                  element={
                    <WhatsappLogin updateCustomerState={updateCustomerState} />
                  }
                />
                <Route
                  path="/signup"
                  element={
                    <CustomerSignup updateCustomerState={updateCustomerState} />
                  }
                />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/register" element={<ResgisterScene />} />
                <Route path="*" element={<HomePage />} />
              </Routes>
              )}
            </NotificationProvider>
          </ThemeProvider>
        </ConstantsProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
